import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './RifleScore.css';
import { ArrowLeft, Crosshair } from 'lucide-react';
import { rifleService } from '../../../../../services';

const TrainingSessionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const [form, setForm] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activityMessage, setActivityMessage] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchSession = async () => {
            try {
                const sessionData = await rifleService.getTrainingSession(id);
                setSession(sessionData);
                setForm({
                    sessionType: sessionData.sessionType || '',
                    discipline: sessionData.discipline || '',
                    focus: sessionData.focus || '',
                    sessionDate: sessionData.sessionDate ? new Date(sessionData.sessionDate).toISOString().slice(0, 10) : '',
                    durationMinutes: sessionData.durationMinutes ?? '',
                    roundCount: sessionData.roundCount ?? '',
                    repCount: sessionData.repCount ?? '',
                    equipmentId: sessionData.equipmentId ?? '',
                    drills: sessionData.drills || '',
                    goals: sessionData.goals || '',
                    observations: sessionData.observations || '',
                    notes: sessionData.notes || '',
                    rating: sessionData.rating ?? '',
                    perceivedEffort: sessionData.perceivedEffort ?? '',
                    location: sessionData.location || ''
                });
            } catch (err) {
                console.error('Error loading training session:', err);
                setError('Unable to load this training session.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSession();
    }, [id]);

    const handleSave = async () => {
        if (!form) {
            return;
        }

        try {
            setIsSaving(true);
            setError(null);
            await rifleService.updateTrainingSession(id, {
                sessionType: form.sessionType || null,
                discipline: form.discipline || null,
                focus: form.focus || null,
                sessionDate: form.sessionDate ? new Date(form.sessionDate).toISOString() : new Date().toISOString(),
                durationMinutes: form.durationMinutes === '' ? 0 : Number(form.durationMinutes),
                roundCount: form.roundCount === '' ? null : Number(form.roundCount),
                repCount: form.repCount === '' ? null : Number(form.repCount),
                equipmentId: form.equipmentId === '' ? null : Number(form.equipmentId),
                drills: form.drills || null,
                goals: form.goals || null,
                observations: form.observations || null,
                notes: form.notes || null,
                rating: form.rating === '' ? null : Number(form.rating),
                perceivedEffort: form.perceivedEffort === '' ? null : Number(form.perceivedEffort),
                location: form.location || null
            });

            setSession((current) => ({
                ...current,
                sessionType: form.sessionType || null,
                discipline: form.discipline || null,
                focus: form.focus || null,
                sessionDate: form.sessionDate ? new Date(form.sessionDate).toISOString() : null,
                durationMinutes: form.durationMinutes === '' ? 0 : Number(form.durationMinutes),
                roundCount: form.roundCount === '' ? null : Number(form.roundCount),
                repCount: form.repCount === '' ? null : Number(form.repCount),
                equipmentId: form.equipmentId === '' ? null : Number(form.equipmentId),
                drills: form.drills || null,
                goals: form.goals || null,
                observations: form.observations || null,
                notes: form.notes || null,
                rating: form.rating === '' ? null : Number(form.rating),
                perceivedEffort: form.perceivedEffort === '' ? null : Number(form.perceivedEffort),
                location: form.location || null
            }));
            setActivityMessage('Training session updated');
            setTimeout(() => setActivityMessage(null), 2500);
        } catch (saveError) {
            console.error('Error updating training session:', saveError);
            setError('Could not update this training session.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="rifle-loading">
                <div className="rifle-spinner"></div>
                <p>Loading training session...</p>
            </div>
        );
    }

    if (error || !session) {
        return <div className="rifle-error-message">{error || 'Training session not found.'}</div>;
    }

    return (
        <div className="rifle-score-page">
            <div className="rifle-detail-actions">
                <button className="rifle-secondary-btn" onClick={() => navigate('/dashboard/score/rifle')}>
                    <ArrowLeft size={16} />
                    Back to rifle dashboard
                </button>
            </div>

            <div className="rifle-header">
                <div className="rifle-header-content">
                    <div className="rifle-header-icon">
                        <Crosshair size={28} />
                    </div>
                    <div className="rifle-header-text">
                        <h1>{session.focus || session.sessionType || 'Training session'}</h1>
                        <p>{session.discipline || 'General'} - {session.sessionType || 'Session'}</p>
                    </div>
                </div>
            </div>

            {error ? <div className="rifle-error-message">{error}</div> : null}
            {activityMessage ? <div className="rifle-success-message">{activityMessage}</div> : null}

            <section className="rifle-detail-card">
                <h3>Edit session</h3>
                <div className="rifle-form-grid">
                    <input
                        type="text"
                        placeholder="Session type"
                        value={form?.sessionType || ''}
                        onChange={(event) => setForm((current) => ({ ...current, sessionType: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Discipline"
                        value={form?.discipline || ''}
                        onChange={(event) => setForm((current) => ({ ...current, discipline: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Focus"
                        value={form?.focus || ''}
                        onChange={(event) => setForm((current) => ({ ...current, focus: event.target.value }))}
                    />
                    <input
                        type="date"
                        value={form?.sessionDate || ''}
                        onChange={(event) => setForm((current) => ({ ...current, sessionDate: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="0"
                        placeholder="Duration minutes"
                        value={form?.durationMinutes ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, durationMinutes: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="0"
                        placeholder="Rounds"
                        value={form?.roundCount ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, roundCount: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="0"
                        placeholder="Reps"
                        value={form?.repCount ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, repCount: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Location"
                        value={form?.location || ''}
                        onChange={(event) => setForm((current) => ({ ...current, location: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="1"
                        max="10"
                        placeholder="Rating"
                        value={form?.rating ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, rating: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="1"
                        max="10"
                        placeholder="Effort"
                        value={form?.perceivedEffort ?? ''}
                        onChange={(event) => setForm((current) => ({ ...current, perceivedEffort: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-group">
                    <label>Goals</label>
                    <textarea
                        value={form?.goals || ''}
                        onChange={(event) => setForm((current) => ({ ...current, goals: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-group">
                    <label>Drills</label>
                    <textarea
                        value={form?.drills || ''}
                        onChange={(event) => setForm((current) => ({ ...current, drills: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-group">
                    <label>Observations</label>
                    <textarea
                        value={form?.observations || ''}
                        onChange={(event) => setForm((current) => ({ ...current, observations: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-group">
                    <label>Notes</label>
                    <textarea
                        value={form?.notes || ''}
                        onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                    />
                </div>
                <div className="rifle-form-actions">
                    <button className="rifle-primary-btn" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save session'}
                    </button>
                </div>
            </section>

            <section className="rifle-detail-card">
                <h3>Session details</h3>
                <div className="rifle-detail-list">
                    <p><strong>Date:</strong> {session.sessionDate ? new Date(session.sessionDate).toLocaleDateString() : 'Not set'}</p>
                    <p><strong>Duration:</strong> {session.durationMinutes} minutes</p>
                    <p><strong>Equipment:</strong> {session.equipmentName || 'Not set'}</p>
                    <p><strong>Rounds:</strong> {session.roundCount ?? 'Not recorded'}</p>
                    <p><strong>Reps:</strong> {session.repCount ?? 'Not recorded'}</p>
                    <p><strong>Goals:</strong> {session.goals || 'None'}</p>
                    <p><strong>Drills:</strong> {session.drills || 'None'}</p>
                    <p><strong>Observations:</strong> {session.observations || 'None'}</p>
                    <p><strong>Effort:</strong> {session.perceivedEffort ?? 'Not rated'}</p>
                    <p><strong>Rating:</strong> {session.rating ?? 'Not rated'}</p>
                    <p><strong>Location:</strong> {session.location || 'Not set'}</p>
                    <p><strong>Notes:</strong> {session.notes || 'None'}</p>
                </div>
            </section>
        </div>
    );
};

export default TrainingSessionDetail;
